import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  ListGroup,
  ListGroupItem,
  Badge,
} from 'reactstrap';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import classnames from 'classnames';
import { FirebaseContext } from '../../components/Firebase';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore, OrgCategory, categoryToType } from '../../store/organization.store';
import { useToast } from '../../contexts/ToastContext';
import * as ROUTES from '../../constants/routes';

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

interface SearchResult {
  type: 'org' | 'artist' | 'client' | 'agency';
  id: string;
  name: string;
  linkedOrgId: string | null;
  hasOrg: boolean;
  orgType?: string;
}

const GetStarted: React.FC = () => {
  const history = useHistory();
  const firebase = useContext(FirebaseContext);
  const auth = useAuthStore(state => state.auth);
  const { showSuccess } = useToast();

  const setOrganizations = useOrganizationStore(state => state.setOrganizations);
  const setCurrentOrganization = useOrganizationStore(state => state.setCurrentOrganization);
  const clearOrganization = useOrganizationStore(state => state.clearOrganization);
  const organizations = useOrganizationStore(state => state.organizations);

  // Tab state
  const [activeTab, setActiveTab] = useState('create');

  // Create org state
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgCategory, setNewOrgCategory] = useState<OrgCategory>('artist');
  const [isCreating, setIsCreating] = useState(false);

  // Search state
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<SearchResult | null>(null);
  const debouncedOrgName = useDebounce(newOrgName, 300);

  // Join org state
  const [inviteCode, setInviteCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);

  // Common state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Track if there's a pending claim code
  const [pendingClaimCode, setPendingClaimCode] = useState<string | null>(null);

  // Check for pending invite code or claim code from sessionStorage
  React.useEffect(() => {
    const pendingInvite = sessionStorage.getItem('pendingInviteCode');
    if (pendingInvite) {
      setInviteCode(pendingInvite);
      setActiveTab('join');
      sessionStorage.removeItem('pendingInviteCode');
    }

    // Check for pending claim code
    const pendingClaim = sessionStorage.getItem('pendingClaimCode');
    if (pendingClaim) {
      setPendingClaimCode(pendingClaim);
      // Don't remove it yet - we'll need it after org creation
    }
  }, []);

  // Search for existing orgs/entries when org name changes
  useEffect(() => {
    const searchEntries = async () => {
      if (!firebase || debouncedOrgName.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await firebase.searchOrganizationsAndEntries(debouncedOrgName);
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    searchEntries();
  }, [debouncedOrgName, firebase]);

  // Clear selected entry when org name changes significantly
  useEffect(() => {
    if (selectedEntry && !newOrgName.toLowerCase().includes(selectedEntry.name.toLowerCase().substring(0, 3))) {
      setSelectedEntry(null);
    }
  }, [newOrgName, selectedEntry]);

  // Handle selecting an existing entry to claim/link
  const handleSelectEntry = (entry: SearchResult) => {
    if (entry.type === 'org') {
      // Can't claim an existing org - show message
      setError(`"${entry.name}" already exists as an organization. Use an invite code to join it.`);
      return;
    }

    if (entry.hasOrg) {
      // Already linked to an org
      setError(`"${entry.name}" is already linked to an organization.`);
      return;
    }

    // Select this entry to link with the new org
    setSelectedEntry(entry);
    setNewOrgName(entry.name);
    // Set category based on entry type
    if (entry.type === 'artist') setNewOrgCategory('artist');
    else if (entry.type === 'client') setNewOrgCategory('brand');  // Default client entries to brand
    else if (entry.type === 'agency') setNewOrgCategory('agency');
    setError(null);
  };

  // Handle creating a new organization
  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newOrgName.trim()) {
      setError('Please enter an organization name');
      return;
    }

    if (!firebase || !auth) {
      setError('You must be logged in');
      return;
    }

    setIsCreating(true);
    setError(null);
    setSuccess(null);

    // Derive type from category
    const newOrgType = categoryToType(newOrgCategory);

    try {
      // Create the organization
      const newOrg = await firebase.createOrganization({
        name: newOrgName.trim(),
        type: newOrgType,
        category: newOrgCategory,
        createdBy: auth.uid,
      });

      // Add the current user to the organization as ADMIN
      const updatedOrgs = await firebase.addUserToOrganization(
        auth.uid,
        newOrg.id,
        newOrg.name,
        'ADMIN',
        newOrgType,
        newOrgCategory
      );

      // Also add to members subcollection
      const { setDoc, doc } = await import('firebase/firestore');
      await setDoc(doc(firebase.db, 'organizations', newOrg.id, 'members', auth.uid), {
        joinedAt: new Date().toISOString(),
        role: 'ADMIN',
        invitedBy: null,
        inviteCode: null,
      });

      // Handle global entry linking/creation
      // Map category to the global collection type
      const globalEntryType = newOrgCategory === 'artist' || newOrgCategory === 'publisher'
        ? 'artist'
        : newOrgCategory === 'agency'
          ? 'agency'
          : 'client';

      if (selectedEntry && !selectedEntry.hasOrg) {
        // Link existing entry to new org
        await firebase.linkGlobalEntryToOrg(
          selectedEntry.type as 'artist' | 'client' | 'agency',
          selectedEntry.id,
          newOrg.id
        );
      } else {
        // Create new global entry for the org
        await firebase.createGlobalEntryForOrg(newOrg.id, newOrg.name, globalEntryType);
      }

      // Update the organization store
      setOrganizations(updatedOrgs);
      setCurrentOrganization(newOrg.id, newOrg.name, 'ADMIN', newOrgType, newOrgCategory);

      // Persist the selection
      await firebase.setUserCurrentOrg(auth.uid, newOrg.id);

      // Show success toast with category label
      const categoryLabels: Record<OrgCategory, string> = {
        artist: 'Artist',
        publisher: 'Publisher',
        agency: 'Agency',
        brand: 'Brand',
        production: 'Production',
      };
      showSuccess(`${categoryLabels[newOrgCategory]} organization "${newOrg.name}" created successfully!`);

      // Check if there's a pending claim code - redirect to claim page
      const claimCode = sessionStorage.getItem('pendingClaimCode');
      if (claimCode) {
        sessionStorage.removeItem('pendingClaimCode');
        history.push(`/claim/${claimCode}`);
      } else {
        // Redirect to the library
        history.push(ROUTES.FIND_AUDIO);
      }
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setError(err.message || 'Failed to create organization');
      setIsCreating(false);
    }
  };

  // Handle joining an organization with invite code
  const handleJoinOrganization = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    if (!firebase || !auth) {
      setError('You must be logged in');
      return;
    }

    setIsJoining(true);
    setError(null);
    setSuccess(null);

    try {
      // Use the invite (pass email for validation)
      const invite = await firebase.useInvite(inviteCode.trim().toUpperCase(), auth.uid, auth.email);

      // Refresh user's organizations
      const updatedOrgs = await firebase.getUserOrganizations(auth.uid);
      setOrganizations(updatedOrgs);

      // Set this as the current organization (invite now includes orgType and orgCategory)
      setCurrentOrganization(invite.orgId, invite.orgName, invite.role, invite.orgType || 'artist', invite.orgCategory);

      // Persist the selection
      await firebase.setUserCurrentOrg(auth.uid, invite.orgId);

      // Show success toast
      showSuccess(`Successfully joined "${invite.orgName}"!`);

      // Redirect to the library
      history.push(ROUTES.FIND_AUDIO);
    } catch (err: any) {
      console.error('Error joining organization:', err);
      setError(err.message || 'Failed to join organization');
      setIsJoining(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    if (!firebase) return;

    clearOrganization();
    await firebase.doSignOut();
    history.push(ROUTES.SIGN_IN);
  };

  // If user already has orgs, show option to go back
  const hasOrgs = organizations.length > 0;

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card>
            <CardBody>
              <CardTitle tag="h3" className="text-white text-center mb-4">
                Get Started
              </CardTitle>

              <p className="text-muted text-center mb-4">
                Create a new organization or join an existing one.
              </p>

              {pendingClaimCode && (
                <Alert color="info" className="mb-4">
                  <i className="tim-icons icon-notes mr-2"></i>
                  You have a license waiting to be claimed. Create or join an organization to proceed.
                </Alert>
              )}

              {error && (
                <Alert color="danger" toggle={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" toggle={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              <Nav tabs className="mb-4" style={{ borderBottom: 'none', gap: '1.5rem' }}>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'create' })}
                    onClick={() => { setActiveTab('create'); setError(null); }}
                    style={{
                      cursor: 'pointer',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'create' ? '2px solid var(--color-accent)' : '2px solid transparent',
                      borderRadius: 0,
                      color: activeTab === 'create' ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                      padding: '0.5rem 0',
                      display: 'inline'
                    }}
                  >
                    Create Organization
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'join' })}
                    onClick={() => { setActiveTab('join'); setError(null); }}
                    style={{
                      cursor: 'pointer',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'join' ? '2px solid var(--color-accent)' : '2px solid transparent',
                      borderRadius: 0,
                      color: activeTab === 'join' ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                      padding: '0.5rem 0',
                      display: 'inline'
                    }}
                  >
                    Join Organization
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="create">
                  <Form onSubmit={handleCreateOrganization}>
                    <FormGroup tag="fieldset">
                      <Label className="text-white">
                        Organization Type
                      </Label>
                      <div className="d-flex flex-wrap" style={{ gap: '0.75rem' }}>
                        {[
                          { value: 'artist', label: 'Artist', disabledCheck: selectedEntry !== null && selectedEntry.type !== 'artist' },
                          { value: 'publisher', label: 'Publisher', disabledCheck: selectedEntry !== null && selectedEntry.type !== 'artist' },
                          { value: 'brand', label: 'Brand', disabledCheck: selectedEntry !== null && !['client', 'agency'].includes(selectedEntry.type) },
                          { value: 'agency', label: 'Agency', disabledCheck: selectedEntry !== null && selectedEntry.type !== 'agency' },
                          { value: 'production', label: 'Production', disabledCheck: selectedEntry !== null && !['client', 'agency'].includes(selectedEntry.type) },
                        ].map((option) => {
                          const isDisabled = isCreating || option.disabledCheck;
                          const isSelected = newOrgCategory === option.value;
                          return (
                            <label
                              key={option.value}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                opacity: isDisabled ? 0.5 : 1,
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                onChange={() => {
                                  if (!isDisabled) {
                                    setNewOrgCategory(option.value as OrgCategory);
                                    setSelectedEntry(null);
                                  }
                                }}
                                disabled={isDisabled}
                                style={{ padding: 4 }}
                                icon={<CheckBoxOutlineBlankIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />}
                                checkedIcon={<CheckBoxIcon style={{ color: '#fff', fontSize: 20 }} />}
                              />
                              <span
                                className="text-white"
                                style={{ color: isSelected ? '#fff' : 'rgba(255,255,255,0.7)' }}
                              >
                                {option.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <small className="text-muted d-block mt-2">
                        {newOrgCategory === 'artist' || newOrgCategory === 'publisher'
                          ? 'Upload, manage, and license your audio assets'
                          : 'Access and manage licensed assets from artists'}
                      </small>
                    </FormGroup>
                    <FormGroup>
                      <Label for="orgName" className="text-white">
                        Organization Name
                      </Label>
                      <Input
                        type="text"
                        id="orgName"
                        placeholder={
                          newOrgCategory === 'artist' || newOrgCategory === 'publisher'
                            ? 'e.g., My Studio, Personal Vault'
                            : 'e.g., Acme Corp, Brand Name'
                        }
                        value={newOrgName}
                        onChange={(e) => { setNewOrgName(e.target.value); setSelectedEntry(null); }}
                        disabled={isCreating}
                      />

                      {/* Search Results */}
                      {newOrgName.length >= 2 && (isSearching || searchResults.length > 0) && !selectedEntry && (
                        <div
                          style={{
                            marginTop: '0.5rem',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '4px',
                            backgroundColor: 'var(--chatbot-color-2)',
                          }}
                        >
                          {isSearching ? (
                            <div className="text-center py-3">
                              <Spinner size="sm" /> <span className="text-muted ml-2">Searching...</span>
                            </div>
                          ) : searchResults.length > 0 ? (
                            <ListGroup flush>
                              {searchResults.map((result) => (
                                <ListGroupItem
                                  key={`${result.type}-${result.id}`}
                                  action
                                  onClick={() => handleSelectEntry(result)}
                                  style={{
                                    backgroundColor: 'transparent',
                                    borderColor: 'rgba(255,255,255,0.1)',
                                    cursor: result.hasOrg ? 'not-allowed' : 'pointer',
                                    opacity: result.hasOrg ? 0.6 : 1,
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <span className="text-white">{result.name}</span>
                                      <Badge
                                        color={result.type === 'org' ? 'primary' : result.type === 'artist' ? 'info' : result.type === 'agency' ? 'warning' : 'success'}
                                        className="ml-2"
                                        style={{ fontSize: '0.65rem' }}
                                      >
                                        {result.type === 'org' ? result.orgType || 'org' : result.type}
                                      </Badge>
                                    </div>
                                    {result.hasOrg ? (
                                      <small className="text-muted">Already linked</small>
                                    ) : (
                                      <small className="text-success">Click to claim</small>
                                    )}
                                  </div>
                                </ListGroupItem>
                              ))}
                            </ListGroup>
                          ) : (
                            <div className="text-muted text-center py-2 small">
                              No existing entries found. A new one will be created.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Selected Entry Badge */}
                      {selectedEntry && (
                        <div className="mt-2">
                          <Badge color="success" className="p-2">
                            Linking to existing {selectedEntry.type}: "{selectedEntry.name}"
                            <button
                              type="button"
                              className="btn-close btn-close-white ms-2"
                              style={{ fontSize: '0.5rem' }}
                              onClick={() => setSelectedEntry(null)}
                              aria-label="Clear"
                            />
                          </Badge>
                        </div>
                      )}
                    </FormGroup>
                    <Button
                      className="btn-info-custom"
                      type="submit"
                      block
                      disabled={isCreating || !newOrgName.trim()}
                      style={{ position: 'relative', zIndex: 2 }}
                    >
                      <span style={{ position: 'relative', zIndex: 3 }}>
                        {isCreating ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Creating...
                          </>
                        ) : selectedEntry ? (
                          `Create & Link to ${selectedEntry.type}`
                        ) : (
                          'Create Organization'
                        )}
                      </span>
                    </Button>
                  </Form>
                  <p className="text-muted small mt-3 mb-0">
                    You'll be the administrator of this organization.
                    {!selectedEntry && newOrgName.trim() && (
                      <> A new {newOrgCategory === 'artist' || newOrgCategory === 'publisher' ? 'artist' : newOrgCategory === 'agency' ? 'agency' : 'client'} entry will be created and linked.</>
                    )}
                  </p>
                </TabPane>

                <TabPane tabId="join">
                  <Form onSubmit={handleJoinOrganization}>
                    <FormGroup>
                      <Label for="inviteCode" className="text-white">
                        Invite Code
                      </Label>
                      <Input
                        type="text"
                        id="inviteCode"
                        placeholder="Enter 8-character code"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                        disabled={isJoining}
                        maxLength={8}
                        style={{ textTransform: 'uppercase', letterSpacing: '2px' }}
                      />
                    </FormGroup>
                    <Button
                      className="btn-success-custom"
                      type="submit"
                      block
                      disabled={isJoining || !inviteCode.trim()}
                      style={{ position: 'relative', zIndex: 2 }}
                    >
                      <span style={{ position: 'relative', zIndex: 3 }}>
                        {isJoining ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Joining...
                          </>
                        ) : (
                          'Join Organization'
                        )}
                      </span>
                    </Button>
                  </Form>
                  <p className="text-muted small mt-3 mb-0">
                    Ask an organization admin for an invite code.
                  </p>
                </TabPane>
              </TabContent>

              <hr className="my-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              <div className="d-flex justify-content-between">
                <Button color="link" className="text-muted p-0" onClick={handleSignOut}>
                  Sign Out
                </Button>
                {hasOrgs && (
                  <Button
                    color="link"
                    className="text-muted p-0"
                    onClick={() => history.push(ROUTES.SELECT_ORGANIZATION)}
                  >
                    Back to Organizations
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default GetStarted;
